/**
 * Helpers for the inline "one-off override" affordance: translating a Target +
 * Subject + Level into a direct grant rule (priority 90), and matching existing
 * direct grants so an override can be replaced or reset. Centralized here so the
 * sidebar quick-set never reconstructs rule shapes by hand.
 */

import type { Level, Rule, Scope, Subject, Target } from "./types";

export const DIRECT_OVERRIDE_PRIORITY = 90;

export function targetToDirectScope(target: Target): Scope {
	switch (target.kind) {
		case "node":
			return { kind: "node", nodeId: target.nodeId };
		case "property":
			return { kind: "property", nodeId: target.nodeId, propId: target.propId };
		case "edge":
			return { kind: "edge", edgeId: target.edgeId };
		case "edgeProp":
			return { kind: "edgeProp", edgeId: target.edgeId, propId: target.propId };
	}
}

export function describeTarget(target: Target): string {
	switch (target.kind) {
		case "node":
			return "this node";
		case "property":
			return target.propName;
		case "edge":
			return "this relationship";
		case "edgeProp":
			return target.propName;
	}
}

function sameSubject(a: Subject, b: Subject): boolean {
	if (a.kind !== b.kind) return false;
	if (a.kind === "everyone" || b.kind === "everyone") return true;
	return "id" in a && "id" in b && a.id === b.id;
}

function sameScope(a: Scope, b: Scope): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

export function isDirectOverride(
	rule: Rule,
	target: Target,
	subject: Subject,
): boolean {
	return (
		rule.source === "direct" &&
		sameSubject(rule.subject, subject) &&
		sameScope(rule.scope, targetToDirectScope(target))
	);
}

export function buildDirectRule(
	target: Target,
	subject: Subject,
	level: Level,
): Omit<Rule, "id"> {
	// Name node/property overrides with the record so duplicates on the same
	// field (e.g. two "Override · Email") are self-describing in the rules list.
	const recordName =
		target.kind === "node" || target.kind === "property"
			? target.nodeName
			: undefined;
	const label = describeTarget(target);
	const name = recordName
		? target.kind === "node"
			? `Override · ${recordName}`
			: `Override · ${label} · ${recordName}`
		: `Override · ${label}`;
	return {
		name,
		subject,
		level,
		scope: targetToDirectScope(target),
		priority: DIRECT_OVERRIDE_PRIORITY,
		source: "direct",
	};
}
